import orderBy from 'lodash/orderBy';
import { Box, TableBody, TableCell, TableHead, TableRow, TableSortLabel, Typography } from '@mui/material';
import React, { useMemo, useState } from 'react';
import OGTable from './table';
import { groupRecords, normalizeColumns, parseColumnFilter } from '../services/startListColumns';
import { pluralize } from '../services/lang';

const styles = {
    /**
     * Both of a group's structural rows live inside its `tbody`, so OGTable's
     * `tbody tr:nth-of-type(odd)` striping would tint them.
     *
     * Its `tbody td { border-bottom: 0 }` rule is scoped to `td`, so the header
     * cells — rendered as `th` — keep their underline while data rows stay
     * borderless. Its `thead th` weight rule cannot reach them, hence the
     * second selector.
     */
    groupedTable: {
        '& tbody tr.group-heading-row, & tbody tr.group-header-row': {
            backgroundColor: 'transparent !important'
        },
        '& tbody tr.group-header-row th': {
            fontWeight: '600 !important'
        }
    },
    // Carries the gap that used to sit between one table per group. The first
    // group needs no leading space, since the page heading is right above it.
    groupHeadingCell: {
        paddingLeft: 0,
        paddingTop: 3,
        paddingBottom: 0,
        'tbody:first-of-type &': {
            paddingTop: 0
        }
    },
    groupCount: {
        fontSize: '0.7em',
        color: 'text.secondary'
    }
};

/**
 * Splits "40 Miles - 12 Athletes" into its two parts so the count can be
 * de-emphasised against the group value.
 *
 * `separator` is empty when the value is blank, which happens for the trailing
 * group of records that have no value for the grouped column.
 */
const groupHeadingParts = (label, count) => {
    const value = `${label ?? ''}`;
    return {
        value,
        separator: value.length > 0 ? ' - ' : '',
        countText: `${count} ${pluralize('Athlete', 'Athletes', count)}`
    };
};

// One heading above a group's table, with the count subordinate to the value.
const GroupHeading = ({ label, count }) => {
    const { value, separator, countText } = groupHeadingParts(label, count);
    return (
        <Typography variant="h5">
            {value}
            <Box component="span" sx={styles.groupCount}>
                {separator}{countText}
            </Box>
        </Typography>
    );
};

/**
 * Sort state for one set of rows, seeded to the first column.
 *
 * State is per-instance, so one instance per group is what keeps groups sorting
 * independently now that they all share a single table. `records` arrives
 * already ordered; the active column's `sortFns` are layered on top of that
 * ordering.
 */
const useColumnSort = (columns, records) => {
    const [order, setOrder] = useState('asc');
    const [orderByCol, setOrderByCol] = useState(columns[0]?.key);

    const createSortHandler = (property) => () => {
        const isAsc = orderByCol === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderByCol(property);
    };

    const sorted = useMemo(() => {
        const sortFns = columns.find(column => column.key === orderByCol)?.sortFns ?? [];
        return orderBy(records, sortFns, sortFns.map(() => order));
    }, [records, columns, order, orderByCol]);

    return { order, orderByCol, createSortHandler, sorted };
};

/**
 * The header cells for one sortable header row.
 *
 * `component` is left unset inside a `TableHead`, where MUI infers `th` from
 * context. A repeated header row sits inside a `TableBody`, which infers `td`
 * and drops `scope`, so that caller passes all three of `component`, `scope`
 * and `variant` explicitly.
 */
const SortableHeaderCells = ({
    columns = [],
    order,
    orderByCol,
    createSortHandler,
    component
}) => columns.map(column => (
    <TableCell
        key={column.key}
        component={component}
        scope={component ? 'col' : undefined}
        variant={component ? 'head' : undefined}
    >
        <TableSortLabel
            active={orderByCol === column.key}
            direction={orderByCol === column.key ? order : 'asc'}
            onClick={createSortHandler(column.key)}
        >
            {column.header}
        </TableSortLabel>
    </TableCell>
));

const DataRows = ({ columns = [], records = [] }) => records.map((result, i) => (
    <TableRow key={i}>
        {columns.map(column => (
            <TableCell key={column.key}>{column.render(result)}</TableCell>
        ))}
    </TableRow>
));

/**
 * Renders one sortable table over a fixed set of rows, for the ungrouped case.
 */
const StartListTable = ({
    columns = [],
    records = []
}) => {
    const { order, orderByCol, createSortHandler, sorted } = useColumnSort(columns, records);

    return (
        <OGTable>
            <TableHead>
                <TableRow>
                    <SortableHeaderCells
                        columns={columns}
                        order={order}
                        orderByCol={orderByCol}
                        createSortHandler={createSortHandler}
                    />
                </TableRow>
            </TableHead>
            <TableBody>
                <DataRows columns={columns} records={sorted} />
            </TableBody>
        </OGTable>
    );
};

/**
 * One group's rows as its own `tbody`: a heading row, a repeated header row,
 * then the data rows.
 *
 * A `tbody` rather than a fragment, for two reasons. Only one `thead` is valid
 * per table, so the repeated header has to live here; and bare rows under a
 * `table` get hoisted into a single implicit `tbody`, which would merge every
 * group into one striping scope.
 *
 * The two structural rows are always present, including for the trailing blank
 * group, so the striping offset is a constant 2 and `:nth-of-type` — which
 * restarts per `tbody` — still lands on the same data rows as it did when each
 * group had its own table.
 */
const GroupSection = ({ label, count, columns = [], records = [] }) => {
    const { order, orderByCol, createSortHandler, sorted } = useColumnSort(columns, records);

    return (
        <TableBody>
            <TableRow className="group-heading-row">
                <TableCell
                    colSpan={Math.max(columns.length, 1)}
                    sx={styles.groupHeadingCell}
                >
                    <GroupHeading label={label} count={count} />
                </TableCell>
            </TableRow>
            <TableRow className="group-header-row">
                <SortableHeaderCells
                    columns={columns}
                    order={order}
                    orderByCol={orderByCol}
                    createSortHandler={createSortHandler}
                    component="th"
                />
            </TableRow>
            <DataRows columns={columns} records={sorted} />
        </TableBody>
    );
};

/**
 * Renders a sortable start-list table driven by admin-authored custom columns.
 *
 * `baseColumns` are the fixed, provider-specific columns
 * (`{ key, label, valueFn(record), sortFns:[fn] }`). `customColumns` and
 * `customQuestions` come from event config; their `body` strings are compiled
 * with `new Function(arguments, body)(record)`. `resolveQuestion(record, cq)`
 * maps a custom-question config entry to its answer for a given record.
 *
 * `columns` is the optional config-authored visibility list (`['-team']`); see
 * `parseColumnFilter`. Every column renders unless explicitly hidden.
 *
 * `groupBy` optionally names one column to split the list on. That column moves
 * out of the table and its value becomes a heading above each group's rows.
 *
 * Every group shares a single table, because browsers size columns per `table`
 * element — one table per group put the same column at a different horizontal
 * position in each. Each group is a `tbody` carrying its own header row, so it
 * still sorts independently.
 */
const CustomColumnStartList = ({
    records = [],
    baseColumns = [],
    customColumns = [],
    customQuestions = [],
    columns = [],
    resolveQuestion = () => undefined,
    defaultSort = [],
    groupBy = null
}) => {
    const allColumns = useMemo(
        () => normalizeColumns(baseColumns, customColumns, customQuestions),
        [baseColumns, customColumns, customQuestions]
    );

    // Resolved against every column rather than the visible ones, so a column
    // can be both hidden and grouped on.
    const groupColumn = useMemo(
        () => (groupBy ? allColumns.find(column => column.key === groupBy) : undefined),
        [allColumns, groupBy]
    );

    // Only `hidden` affects visibility — columns are shown by default, so the
    // parser's `shown` set has nothing to add here. The grouped column is
    // dropped before the sort seed is read, so a group's initial sort column is
    // the first remaining one.
    const visibleColumns = useMemo(() => {
        const { hidden } = parseColumnFilter(columns);
        return allColumns.filter(column =>
            !hidden.has(column.key) && column.key !== groupColumn?.key
        );
    }, [allColumns, columns, groupColumn]);

    // Hidden columns are still expanded onto each row so that `defaultSort` and
    // grouping can reference them.
    const expandedRecords = useMemo(() => {
        const expanded = records.map(record => ({
            ...record,
            ...Object.fromEntries(
                customColumns.map(cc => [
                    cc.key,
                    new Function(cc.arguments, cc.body)(record)
                ])
            ),
            ...Object.fromEntries(
                customQuestions.map(cq => [
                    cq.key,
                    resolveQuestion(record, cq)
                ])
            )
        }));
        return orderBy(expanded, defaultSort);
    }, [records, customColumns, customQuestions]);

    const groups = useMemo(
        () => (groupColumn ? groupRecords(expandedRecords, groupColumn) : []),
        [expandedRecords, groupColumn]
    );

    if (!groupColumn) {
        return <StartListTable columns={visibleColumns} records={expandedRecords} />;
    }

    return (
        <OGTable sx={styles.groupedTable}>
            {groups.map(group => (
                <GroupSection
                    key={group.key}
                    label={group.label}
                    count={group.records.length}
                    columns={visibleColumns}
                    records={group.records}
                />
            ))}
        </OGTable>
    );
};

export default CustomColumnStartList;
